import { clients } from '../../util/clients';

const { backendRest } = clients.direct;

const commonConfig = {
  headers: {
    'Content-type': 'application/json',
  },
  timeout: 240000,
};

const RESTORE_POLL_MS = 2500;
const RESTORE_MAX_WAIT_MS = 2 * 60 * 60 * 1000; // 2 hours

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const getRestoreRoute = (group) => (group === 'tmc-local-backend' ? 'backend' : 'auth');

const getErrorMessage = (error, fallback = 'Unknown error') => {
  if (error && error.response && error.response.data && error.response.data.message) {
    return error.response.data.message;
  }
  if (error && error.message) {
    return error.message;
  }
  return fallback;
};

const clearRestoreCaches = async (dispatch) => {
  const cacheGroups = [
    'ODFYDeptCache', 'CacheFundFY', 'CacheAccBalance', 'WarFundAccountBalanceDulicateEntries',
    'clearWarGlobalValues', 'clearPayrollGlobalValues', 'clearMiscGlobalValues', 'clearMtgGlobalValues',
  ];

  try {
    const cacheRes = await backendRest.get(`cache-utility/clear-cache?group=${cacheGroups}`);
    if (cacheRes && cacheRes.data && cacheRes.data.status === 'ok') {
      dispatch('flashSuccess', 'Cache got successfully cleared.', { root: true });
      return;
    }
    dispatch('flashError', 'There is some issue on clearing cache.', { root: true });
  } catch (cacheErr) {
    dispatch('logException', cacheErr, { root: true });
    dispatch('flashError', 'There is some issue on clearing cache.', { root: true });
  }
};

const handleRestoreCompleted = async ({
  backup,
  route,
  startConfig,
  commit,
  dispatch,
}) => {
  try {
    await backendRest.post('/jobs/restore/audit', {
      backup,
      restoreTarget: route,
    }, startConfig);
  } catch (auditErr) {
    dispatch('logException', auditErr, { root: true });
  }

  await clearRestoreCaches(dispatch);
  commit('setActiveDbOp', { isActive: false, msg: 'Restoring… done!' });
  dispatch('flashInfo', 'Database restore completed.', { root: true });
};

export const jobs = {
  namespaced: true,
  state: () => ({
    group: undefined,
    prefix: undefined,
    collection: undefined,
    sortBy: undefined,
    nsInclude: '*',
    backup: undefined,
    backups: [],
    totalSizeStr: '',
    activeDbOp: false,
    activeDbOpMsg: '',
    restoreLogs: [],
  }),
  mutations: {
    setBackup(state, backup) {
      state.backup = backup;
    },
    setBackups(state, [files, totalSizeStr]) {
      state.backups = files;
      state.totalSizeStr = totalSizeStr;
    },
    setGroup(state, group) {
      state.group = group;
    },
    setPrefix(state, prefix) {
      state.prefix = prefix;
    },
    setNsInclude(state, nsInclude) {
      state.nsInclude = nsInclude;
    },
    setActiveDbOp(state, { isActive, msg }) {
      state.activeDbOp = isActive;
      state.activeDbOpMsg = msg;
    },
    setCollection(state, collection) {
      state.collection = collection;
    },
    setSortBy(state, sortBy) {
      state.sortBy = sortBy;
    },
    setRestoreLogs(state, logs) {
      state.restoreLogs = logs;
    },
  },
  actions: {
    async listDumps({ state, commit, dispatch }) {
      try {
        await dispatch('reAuth', null, { root: true });
        const { group, sortBy } = state;

        /* build query for url */
        let queryObject = { group };
        if (sortBy) {
          queryObject = { ...queryObject, sortBy };
        }
        const queryParams = new URLSearchParams(queryObject).toString();

        const response = await backendRest.get(`/jobs/dumps/list?${queryParams}`);
        if (response && response.data && response.data.status === 'ok') {
          const { files, totalSizeStr } = response.data;
          commit('setBackups', [files || [], totalSizeStr]);
        } else {
          dispatch('flashError', 'There was a problem listing backups.', { root: true });
        }
      } catch (e) {
        dispatch('logException', e, { root: true });
        dispatch('flashError', 'There was a problem listing backups.', { root: true });
      }
    },
    async backup({ state, commit, dispatch }) {
      try {
        const isTestCafe = localStorage.getItem('isTestCafe') || '0';
        if (isTestCafe !== '0') {
          return true; // bypass backups for TestCafe automated testing
        }
        commit('setActiveDbOp', { isActive: true, msg: 'Backing up...' });
        await dispatch('reAuth', null, { root: true });
        const { group, prefix, collection } = state;
        /* after getting 'collection' value, reset it */
        commit('setCollection', undefined);
        const body = {
          // don't pass group, let the route name choose the group
          prefix,
          collection,
        };
        const route = group === 'tmc-local-backend' ? 'backend' : 'auth';
        const response = await backendRest.post(`/jobs/dump/${route}`, body, commonConfig);
        if (response && response.data && response.data.status === 'ok') {
          dispatch('listDumps');
          commit('setActiveDbOp', { isActive: false, msg: 'Backing up...done!' });
          dispatch('flashInfo', 'Backing up...done!', { root: true });
          return true;
        }
        dispatch('flashError', 'There was a problem performing a backup.', { root: true });
        return false;
      } catch (e) {
        dispatch('logException', e, { root: true });
        dispatch('flashError', 'There was a problem performing a backup.', { root: true });
        commit('setActiveDbOp', { isActive: false, msg: 'There was a problem performing a backup.' });
        return false;
      }
    },
    async restore({ state, commit, dispatch }, params) {
      const setActiveDbOp = (isActive, msg) => commit('setActiveDbOp', { isActive, msg });

      try {
        setActiveDbOp(true, 'Starting restore…');
        await dispatch('reAuth', null, { root: true });

        const { group, backup, nsInclude } = state;
        if (!backup) {
          throw new Error('No backup file selected. Please select a backup from the list before restoring.');
        }

        const route = getRestoreRoute(group);
        const body = { backup, nsInclude, ...params };
        const startConfig = {
          headers: { 'Content-type': 'application/json' },
          timeout: 90000,
        };

        const response = await backendRest.post(`/jobs/restore-async/${route}`, body, startConfig);
        if (!response || !response.data || !response.data.jobId || response.data.status !== 'ok') {
          dispatch('flashError', 'There was a problem starting the DB restore.', { root: true });
          setActiveDbOp(false, 'Could not start restore.');
          return false;
        }

        const { jobId } = response.data;
        setActiveDbOp(true, 'Restore running… (you can wait on this page)');

        const pollConfig = { timeout: 20000 };
        const deadline = Date.now() + RESTORE_MAX_WAIT_MS;

        while (Date.now() < deadline) {
          await sleep(RESTORE_POLL_MS);

          let statusRes;
          try {
            statusRes = await backendRest.get(`/jobs/restore/status/${jobId}`, pollConfig);
          } catch (pollErr) {
            const msg = (pollErr && pollErr.response && pollErr.response.status === 404)
              ? 'Restore status lost (server may have restarted). Check data and logs.'
              : 'Lost connection while checking restore status.';
            setActiveDbOp(false, msg);
            dispatch('flashError', msg, { root: true });
            return false;
          }

          const job = statusRes && statusRes.data && statusRes.data.job;
          if (!job) {
            setActiveDbOp(false, 'Invalid status response.');
            return false;
          }

          const label = job.message || job.restoreStatus || '…';
          setActiveDbOp(true, `Restore: ${label}`);

          if (job.restoreStatus === 'completed') {
            await handleRestoreCompleted({
              backup,
              route,
              startConfig,
              commit,
              dispatch,
            });
            return true;
          }

          if (job.restoreStatus === 'failed') {
            const errorMsg = job.message || 'Restore failed.';
            alert(`STOP!\n${errorMsg}\nAs a result, the restore may be incomplete.`);
            dispatch('flashError', 'There was a problem performing a DB restore.', { root: true });
            setActiveDbOp(false, errorMsg);
            return false;
          }
        }

        setActiveDbOp(false, 'Restore is taking longer than expected. Check server logs or try again later.');
        dispatch('flashWarn', 'Restore still running or status timed out. Refresh backups after a few minutes.', { root: true });
        return false;
      } catch (e) {
        const errorMsg = getErrorMessage(e);
        alert(`STOP!\n${errorMsg}\nAs a result, no data was restored.`);
        dispatch('logException', e, { root: true });
        dispatch('flashError', 'There was a problem performing a DB restore.', { root: true });
        setActiveDbOp(false, 'There was a problem performing a DB restore.');
        return false;
      }
    },
    async getRestoreLogs({ commit, dispatch }) {
      try {
        await dispatch('reAuth', null, { root: true });
        const response = await backendRest.get('/jobs/restore/log');
        if (response && response.data && response.data.log) {
          const logs = response.data.log.split('\n').filter((log) => log.trim() !== '').map((log, index) => {
            const [event, data, filename, user, dateTime] = log.split(',');
            return {
              id: index,
              event,
              data,
              filename,
              user,
              dateTime: new Date(dateTime),
            };
          })
            .sort((a, b) => b.dateTime - a.dateTime);
          commit('setRestoreLogs', logs);
        }
      } catch (error) {
        console.error('Error fetching restore logs:', error);
      }
    },
  },
  getters: {
    backupFiles(state) { // , getters
      return (state.backups || []).map((b) => b.file);
    },
    restoreLogs(state) {
      return state.restoreLogs;
    },
  },
};

export default jobs;
